import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { LinkedMillingJobTabs } from "@/components/blanks/linked-milling-jobs";
import { MqttSuspense } from "@/components/generic/mqtt-suspense";
import { MultiPropertyCard } from "@/components/generic/multi-property-card";

import type { MagazineSlot } from "@/data-interfaces/blanks";
import type { ApiError } from "@/data-interfaces/generics";
import { useMQTTAutoRefreshPublish, useMQTTSubscribe } from "@/hooks/use-mqtt";
import { getMagazineBlankProperties } from "@/lib/data-remappings/blanks";
import {
	BLANKS_PAGE_URL,
	getAppWrappedPageUrl,
	MAGAZINE_SLOT_DETAILS_PAGE_URL,
} from "@/lib/page-routes";
import {
	BL_MAGAZINE_SLOT_LINKED_MILLING_JOBS_URL,
	BL_MAGAZINE_SLOT_URL,
} from "@/lib/request-paths";

export const Route = createFileRoute(
	getAppWrappedPageUrl(MAGAZINE_SLOT_DETAILS_PAGE_URL),
)({
	component: MagazineSlotDetails,
	validateSearch: (search: MagazineSlot) => search,
});

function MagazineSlotDetails() {
	const [t, _] = useTranslation();

	const navigate = useNavigate();

	const magazineSlotSearchParam: MagazineSlot = Route.useSearch();

	const [magazineSlot, setMagazineSlot] = useState<MagazineSlot | ApiError>(
		magazineSlotSearchParam,
	);

	useMQTTSubscribe(BL_MAGAZINE_SLOT_URL, setMagazineSlot);

	useMQTTAutoRefreshPublish(30, BL_MAGAZINE_SLOT_URL, {
		magazine_slot_id: magazineSlotSearchParam.id,
	});

	return (
		<div className="text-white p-8 flex flex-col flex-1 gap-y-4">
			<h2 className="text-2xl sm:text-3xl hover:text-[#238b8c]">
				<button
					type="button"
					onClick={() => {
						navigate({
							to: BLANKS_PAGE_URL,
							search: { selectedTab: "magazine" },
						});
					}}
				>
					{"< "}
					{t("Blanks")}
				</button>
			</h2>

			<div className="flex flex-col gap-y-4">
				<h2 className="text-2xl sm:text-3xl">{t("Selected magazine slot")}</h2>

				<MqttSuspense
					objects={magazineSlot}
					requestUrl={BL_MAGAZINE_SLOT_URL}
					parameters={{ magazine_slot_id: magazineSlotSearchParam.id }}
					genericError="Error retrieving magazine slot"
				>
					{(magazineSlot) => {
						return (
							magazineSlot.blank && (
								<MultiPropertyCard
									multiPropertyObject={{
										title: magazineSlot.id.toString(),
										properties: getMagazineBlankProperties(magazineSlot.blank),
									}}
								/>
							)
						);
					}}
				</MqttSuspense>
			</div>

			<div className="flex flex-1 flex-col grow justify-center">
				<div className="text-2xl sm:text-3xl">{t("Associated jobs")}</div>

				<LinkedMillingJobTabs
					requestUrl={BL_MAGAZINE_SLOT_LINKED_MILLING_JOBS_URL}
					parameters={{ magazine_slot_id: magazineSlotSearchParam.id }}
				/>
			</div>
		</div>
	);
}
